import * as React from "react";
import {createContext, PropsWithChildren, useContext, useEffect} from "react";
import {useAsync} from "react-async-hook";
import {User, useUserApi} from "./api";
import {useLastUserEmail} from "./hooks/useLastUserEmail";
import {LoginState} from "./LoginState";

interface UserContextContent {
	user?: User;
}

const UserContext = createContext<UserContextContent>({});

interface UserProviderProps {
	tenantId?: string;
}

export function UserProvider({tenantId, children}: PropsWithChildren<UserProviderProps>) {
	const {getCurrentUser} = useUserApi();
	const [, setLastEmail] = useLastUserEmail();

	const {loading, error, result} = useAsync(getCurrentUser, []);

	// Store user email for future login hints
	useEffect(() => {
		if (result?.email) {
			setLastEmail(result.email);
		}
	}, [result, setLastEmail]);

	return (
		<LoginState loading={loading} error={error} tenantId={tenantId}>
			<UserContext.Provider value={{user: result}}>
				{children}
			</UserContext.Provider>
		</LoginState>
	);
}

export function useUser() {
	const {user} = useContext(UserContext);
	if (!user) {
		throw new Error("No user found in context. Are you missing a UserProvider in your component tree?");
	}
	return user;
}
